import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import type { Filter, WriteResult } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { DateRangePicker } from "@/components/dates/DateRangePicker";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { gstDefaultPeriod } from "@/lib/gst-period";

function periodFilter(from: string, to: string): Filter {
  return {
    kind: "group" as const,
    combinator: "and" as const,
    children: [
      {
        kind: "condition" as const,
        field: "invoiceDate",
        operator: "gte" as const,
        value: from,
      },
      {
        kind: "condition" as const,
        field: "invoiceDate",
        operator: "lte" as const,
        value: to,
      },
    ],
  };
}

/**
 * Trigger the two server-side exports (ticket #93).
 *
 * Neither export builds anything in this tab. `exportXlsx` and
 * `exportHsnSummary` both return a `WriteResult<{ url: string }>` — a signed
 * URL to a file the server rendered — because a spreadsheet of a year's
 * invoice lines, or the HSN aggregation across them, is exactly the kind of
 * job a browser tab should not be doing: it would hold the whole dataset in
 * memory, block on the main thread, and produce a file nobody but the person
 * who kept that tab open could re-download.
 */
export function Exports() {
  const data = useDataSource();
  const [from, setFrom] = useState(() => gstDefaultPeriod().from);
  const [to, setTo] = useState(() => gstDefaultPeriod().to);

  const filter = periodFilter(from, to);

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="export-period">Period</Label>
          <DateRangePicker
            id="export-period"
            from={from}
            to={to}
            onChange={(range) => {
              setFrom(range.from);
              setTo(range.to);
            }}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <ExportCard
          title="Invoices (.xlsx)"
          description="Every invoice in the period, one row per invoice."
          run={() => data.invoicing.exportXlsx({ filter, limit: 200 })}
        />
        <ExportCard
          title="HSN summary (.xlsx)"
          description="Taxable value and tax aggregated by HSN code, ready for GSTR-1."
          run={() => data.invoicing.exportHsnSummary({ filter, limit: 200 })}
        />
      </div>
    </div>
  );
}

function ExportCard({
  title,
  description,
  run,
}: {
  title: string;
  description: string;
  run: () => Promise<WriteResult<{ url: string }>>;
}) {
  const mutation = useMutation({ mutationFn: run });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Button
          size="sm"
          disabled={mutation.isPending}
          onClick={() => {
            mutation.mutate();
          }}
        >
          {mutation.isPending ? "Generating…" : "Generate"}
        </Button>

        {mutation.data ? (
          mutation.data.ok ? (
            <a
              href={mutation.data.data.url}
              target="_blank"
              rel="noreferrer"
              className="text-primary-strong block text-sm underline underline-offset-4"
            >
              Download
            </a>
          ) : (
            <p className="text-destructive text-sm">
              {mutation.data.rejection.message}
            </p>
          )
        ) : null}

        {mutation.isError ? (
          <p className="text-destructive text-sm">{mutation.error.message}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
